// WPS-006 + WPS-008 + WPS-018 · Card Canvas v4 — HTML 1:1 render of business card.
// v4 features:
//   - Font pairings (display/body/accent) per element role
//   - Body text 10pt minimum enforced (atelier accessibility standard)
//   - cardData.backgroundUrl renders as full-bleed background on front
//   - Print guides toggle: trim line (red) + safe zone (blue dashed)
//   - Real QR via /api/qr endpoint (no placeholder)
//   - Logo with Lucide icon support + image fallback
import React, { useState } from 'react';
import * as LucideIcons from 'lucide-react';

const TIER_STYLES = {
  signature: {
    label: 'Wunli Signature',
    stock: '32pt Cotton · Soft-Touch Matte',
    background: '#ffffff',
    sideBarPct: 4, topBarPct: 0,
    textPrimary: null, textSecondary: '#191919', textMuted: '#6b6b6b',
    showAccentLine: true, qrInvert: false,
  },
  atelier: {
    label: 'Wunli Atelier',
    stock: '38pt Double-Thick Duplex · Spot UV · Edge Paint',
    background: '#fbf8ef',
    sideBarPct: 5.5, topBarPct: 0,
    textPrimary: null, textSecondary: '#1a1a1a', textMuted: '#6b6b6b',
    showAccentLine: true, qrInvert: false,
  },
  executive: {
    label: 'Wunli Executive',
    stock: '48pt Cotton · Letterpress · Foil Stamp',
    background: '#121214',
    sideBarPct: 0, topBarPct: 6,
    textPrimary: '#ffffff', textSecondary: '#ebebeb', textMuted: '#9c9c9c',
    showAccentLine: false, qrInvert: true,
  },
};

const CARD_RATIO_H = 3.375 / 2.125;
const CARD_RATIO_V = 2.125 / 3.375;

const dimsFor = (baseWidth, orientation) => {
  if (orientation === 'vertical') {
    const W = baseWidth * 0.63;
    return { W, H: W / CARD_RATIO_V };
  }
  return { W: baseWidth, H: baseWidth / CARD_RATIO_H };
};

const QR_SIZE_PCT = { small: 0.14, medium: 0.18, large: 0.26 };

// Atelier accessibility · enforce body minimum 10pt (Carlos rule 2026-05-14)
const BODY_MIN_PT = 10;
const ACCENT_MIN_PT = 8;
const DISPLAY_MIN_PT = 14;

// Convert pt to px @ 72dpi. CardCanvas renders at base 350px ≈ 3.5" trim.
// Each pt is roughly W * (1 / 252pt trim width) for horizontal cards.
const ptToPxFactor = (W) => W / 252;

const fontFamilyOf = (style) => {
  if (!style) return 'Inter, system-ui, sans-serif';
  const fam = style.family || 'Inter';
  return `'${fam}', Inter, system-ui, sans-serif`;
};

const buildTextStyle = (style, basePt, minPt, scaleFactor) => {
  const pt = Math.max(minPt, style?.sizeRecommendation || basePt);
  return {
    fontFamily: fontFamilyOf(style),
    fontSize: `${pt * scaleFactor}px`,
    fontWeight: style?.weight || 400,
    fontStyle: style?.style || 'normal',
  };
};

const slugToPascal = (slug) => slug.split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join('');

const LogoRender = ({ logoUrl, size, color }) => {
  if (!logoUrl) return null;
  if (logoUrl.startsWith('lucide:')) {
    const slug = logoUrl.replace('lucide:', '');
    const Comp = LucideIcons[slugToPascal(slug)];
    if (Comp) return <Comp size={size} color={color || '#0a0a0a'} strokeWidth={1.5} />;
  }
  const base = (process.env.REACT_APP_BACKEND_URL || 'https://print.wunli.com').replace(/\/$/, '');
  const fullUrl = logoUrl.startsWith('http') ? logoUrl : `${base}${logoUrl}`;
  return <img src={fullUrl} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />;
};

const QRReal = ({ payload, size, invert }) => {
  if (!payload) return null;
  const base = (process.env.REACT_APP_BACKEND_URL || 'https://print.wunli.com').replace(/\/$/, '');
  const url = `${base}/api/qr?data=${encodeURIComponent(payload)}&size=${Math.round(size * 3)}`;
  return (
    <div style={{
      width: `${size}px`, height: `${size}px`,
      background: invert ? '#ffffff' : '#0a0a0a',
      padding: '2px',
      flex: '0 0 auto',
    }}>
      <img src={url} alt="QR" style={{
        width: '100%', height: '100%', objectFit: 'contain',
        filter: invert ? 'none' : 'invert(1)',
      }} />
    </div>
  );
};

const PrintGuides = ({ W, H, BLEED_PCT = 0.027, SAFE_PCT = 0.083 }) => {
  // Trim line = card edge (red dashed)
  // Safe zone = inside the trim by ~0.125" (blue dashed)
  const bleedPx = W * BLEED_PCT;
  const safePx = W * SAFE_PCT;
  return (
    <div style={{
      position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 100,
    }}>
      {/* Trim line · red */}
      <div style={{
        position: 'absolute',
        top: bleedPx, left: bleedPx,
        right: bleedPx, bottom: bleedPx,
        border: '1px dashed rgba(220,38,38,0.7)',
      }} />
      {/* Safe zone · blue */}
      <div style={{
        position: 'absolute',
        top: safePx, left: safePx,
        right: safePx, bottom: safePx,
        border: '1px dashed rgba(2,132,199,0.7)',
      }} />
      {/* Labels */}
      <div style={{
        position: 'absolute', top: '-14px', left: bleedPx,
        fontSize: '8px', fontFamily: 'JetBrains Mono, monospace',
        letterSpacing: '0.1em', textTransform: 'uppercase',
        color: '#dc2626', fontWeight: 700,
      }}>trim line · cut</div>
      <div style={{
        position: 'absolute', bottom: '-14px', left: safePx,
        fontSize: '8px', fontFamily: 'JetBrains Mono, monospace',
        letterSpacing: '0.1em', textTransform: 'uppercase',
        color: '#0284c7', fontWeight: 700,
      }}>safe zone · keep text inside</div>
    </div>
  );
};

const CardFront = ({ cardData, tier, watermark, scale, orientation, fontPairing, showGuides }) => {
  const t = TIER_STYLES[tier] || TIER_STYLES.signature;
  const colors = cardData.colors || ['#0a0a0a', '#c9a961'];
  const primary = colors[0] || '#0a0a0a';
  const accent = colors[1] || '#c9a961';
  const textPrimary = t.textPrimary || primary;

  const { W, H } = dimsFor(350 * scale, orientation);
  const padX = `${W * 0.054}px`;
  const padY = `${H * 0.094}px`;
  const sf = ptToPxFactor(W);          // scale factor pt → px

  const showQRFront = cardData.qrPosition === 'front';
  const qrSizePct = QR_SIZE_PCT[cardData.qrSize || 'medium'];

  const bgUrl = cardData.backgroundUrl;
  const bgFull = bgUrl && !bgUrl.startsWith('lucide:')
    ? (bgUrl.startsWith('http') ? bgUrl : `${(process.env.REACT_APP_BACKEND_URL || 'https://print.wunli.com').replace(/\/$/, '')}${bgUrl}`)
    : null;

  // Resolve font styles · default to Inter if no pairing
  const fp = fontPairing || {
    display: { family: 'Inter', weight: 800 },
    body:    { family: 'Inter', weight: 600, sizeRecommendation: 11 },
    accent:  { family: 'Inter', weight: 400, style: 'italic' },
  };
  const styleDisplay = buildTextStyle(fp.display, 13, DISPLAY_MIN_PT, sf);
  const styleBody    = buildTextStyle(fp.body,    11, BODY_MIN_PT, sf);
  const styleAccent  = buildTextStyle(fp.accent || fp.body, 9, ACCENT_MIN_PT, sf);
  const styleContact = buildTextStyle(fp.contact || fp.body, 10, BODY_MIN_PT, sf);

  const contactRows = [
    ['E', cardData.email],
    ['T', cardData.phone],
    ['W', cardData.website],
    ['A', cardData.address],
  ].filter(([, v]) => !!v);

  return (
    <div style={{
      width: `${W}px`, height: `${H}px`,
      backgroundColor: t.background,
      position: 'relative', overflow: 'hidden',
      boxShadow: '0 8px 24px rgba(0,0,0,0.18), 0 1px 0 rgba(0,0,0,0.04)',
      borderRadius: 0, color: t.textSecondary,
    }}>
      {/* Background image · WPS-018 */}
      {bgFull && (
        <img src={bgFull} alt="" style={{
          position: 'absolute', inset: 0,
          width: '100%', height: '100%', objectFit: 'cover',
          opacity: 0.85,
        }} />
      )}

      {/* Tier structural overlays */}
      {t.sideBarPct > 0 && (
        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${t.sideBarPct}%`, backgroundColor: primary, opacity: bgFull ? 0.9 : 1 }} />
      )}
      {t.topBarPct > 0 && (
        <div style={{ position: 'absolute', left: 0, top: 0, right: 0, height: `${t.topBarPct}%`, backgroundColor: accent }} />
      )}

      {/* Logo top-right · supports Lucide icon or image */}
      {cardData.logoUrl && (
        <div style={{
          position: 'absolute', right: padX, top: padY,
          width: `${W * 0.16}px`, height: `${W * 0.16}px`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <LogoRender logoUrl={cardData.logoUrl} size={W * 0.14} color={primary} />
        </div>
      )}

      {/* Content within safe zone */}
      <div style={{
        position: 'absolute', left: padX, right: padX, top: padY, bottom: padY,
        display: 'flex', flexDirection: 'column',
      }}>
        {t.showAccentLine && (
          <div style={{ borderTop: `0.6px solid ${accent}`, marginBottom: `${H * 0.03}px` }} />
        )}

        <div style={{ flex: '0 0 auto' }}>
          <div style={{
            ...styleDisplay,
            letterSpacing: '-0.01em', color: textPrimary,
            lineHeight: 1.1, marginBottom: `${H * 0.04}px`,
          }}>{cardData.companyName || 'Your Company'}</div>

          <div style={{
            ...styleBody, color: t.textSecondary, lineHeight: 1.15,
          }}>{cardData.personalName || cardData.customerName || 'Your Name'}</div>

          {cardData.jobTitle && (
            <div style={{
              ...styleAccent, color: t.textMuted, marginTop: '2px',
            }}>{cardData.jobTitle}</div>
          )}
        </div>

        <div style={{
          flex: '1 1 auto', display: 'flex',
          alignItems: 'flex-end', justifyContent: 'space-between',
          gap: '8px', marginTop: `${H * 0.04}px`,
        }}>
          <div style={{ flex: '1 1 auto', minWidth: 0 }}>
            {contactRows.map(([label, val]) => (
              <div key={label} style={{
                display: 'flex', alignItems: 'baseline',
                ...styleContact, lineHeight: 1.4, color: t.textSecondary,
              }}>
                <span style={{ color: accent, fontWeight: 700, width: `${W * 0.03}px`, flex: '0 0 auto' }}>{label}</span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{val}</span>
              </div>
            ))}
            {cardData.slogan && (
              <div style={{
                ...styleAccent, color: accent, marginTop: `${H * 0.03}px`,
              }}>{cardData.slogan}</div>
            )}
          </div>
          {showQRFront && (cardData.qrPayload || cardData.website || cardData.phone) && (
            <QRReal
              payload={cardData.qrPayload || cardData.website || `tel:${cardData.phone}`}
              size={W * qrSizePct}
              invert={t.qrInvert}
            />
          )}
        </div>
      </div>

      {watermark && <Watermark W={W} qrInvert={t.qrInvert} />}
      {showGuides && <PrintGuides W={W} H={H} />}
    </div>
  );
};

const CardBack = ({ cardData, tier, watermark, scale, orientation, fontPairing, showGuides }) => {
  const t = TIER_STYLES[tier] || TIER_STYLES.signature;
  const colors = cardData.colors || ['#0a0a0a', '#c9a961'];
  const primary = colors[0] || '#0a0a0a';
  const accent = colors[1] || '#c9a961';

  const back = cardData.backDesign || { enabled: false };
  const { W, H } = dimsFor(350 * scale, orientation);
  const padX = `${W * 0.054}px`;
  const padY = `${H * 0.094}px`;
  const sf = ptToPxFactor(W);

  const showQRBack = cardData.qrPosition === 'back' || (back.showQR !== false && cardData.qrPosition !== 'none');
  const qrSizePct = QR_SIZE_PCT[cardData.qrSize || 'medium'];

  const bg = back.backgroundColor || t.background;
  const isDark = bg === '#121214' || tier === 'executive';
  const textColor = isDark ? '#ebebeb' : t.textSecondary;
  const headlineColor = isDark ? '#ffffff' : primary;

  const layout = back.layout || 'minimal-text+qr';
  const firstImage = (back.imageUrls || [])[0];

  const fp = fontPairing || {
    display: { family: 'Inter', weight: 800 },
    body:    { family: 'Inter', weight: 400, sizeRecommendation: 11 },
  };
  const styleDisplay = buildTextStyle(fp.display, 14, DISPLAY_MIN_PT, sf);
  const styleBody    = buildTextStyle(fp.body, 11, BODY_MIN_PT, sf);

  return (
    <div style={{
      width: `${W}px`, height: `${H}px`,
      backgroundColor: bg,
      position: 'relative', overflow: 'hidden',
      boxShadow: '0 8px 24px rgba(0,0,0,0.18), 0 1px 0 rgba(0,0,0,0.04)',
      borderRadius: 0, color: textColor,
    }}>
      {layout === 'photo-only' && firstImage && (
        <img src={firstImage} alt="back" style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover',
        }} />
      )}
      {layout === 'image+qr' && firstImage && (
        <img src={firstImage} alt="back" style={{
          position: 'absolute', left: 0, top: 0, bottom: 0, width: '55%', objectFit: 'cover',
        }} />
      )}
      {layout === 'image+text' && firstImage && (
        <img src={firstImage} alt="back" style={{
          position: 'absolute', left: 0, top: 0, right: 0, height: '55%', objectFit: 'cover',
        }} />
      )}

      <div style={{
        position: 'absolute',
        left: (layout === 'image+qr' && firstImage) ? '58%' : padX,
        right: padX,
        top: (layout === 'image+text' && firstImage) ? '58%' : padY,
        bottom: padY,
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
      }}>
        {back.headline && (
          <div style={{
            ...styleDisplay, color: headlineColor,
            letterSpacing: '-0.01em', lineHeight: 1.1,
            marginBottom: `${H * 0.05}px`,
          }}>{back.headline}</div>
        )}
        {back.body && (
          <div style={{
            ...styleBody, color: textColor,
            lineHeight: 1.5, marginBottom: `${H * 0.05}px`, opacity: 0.9,
          }}>{back.body}</div>
        )}

        {showQRBack && (cardData.qrPayload || cardData.website || cardData.phone) && (
          <div style={{
            marginTop: 'auto', display: 'flex',
            justifyContent: 'flex-end', alignItems: 'flex-end',
          }}>
            <QRReal
              payload={cardData.qrPayload || cardData.website || `tel:${cardData.phone}`}
              size={W * qrSizePct}
              invert={isDark}
            />
          </div>
        )}
      </div>

      <div style={{
        position: 'absolute', left: padX, bottom: padY,
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: `${W * 0.018}px`, letterSpacing: '0.15em',
        color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(10,10,10,0.4)',
      }}>wunli.com</div>

      {watermark && <Watermark W={W} qrInvert={isDark} />}
      {showGuides && <PrintGuides W={W} H={H} />}
    </div>
  );
};

const Watermark = ({ W, qrInvert }) => (
  <div style={{
    position: 'absolute', inset: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    pointerEvents: 'none',
  }}>
    <div style={{
      fontSize: `${W * 0.09}px`,
      fontWeight: 900, letterSpacing: '0.25em',
      color: qrInvert ? 'rgba(255,255,255,0.10)' : 'rgba(10,10,10,0.10)',
      transform: 'rotate(-22deg)',
      fontFamily: 'JetBrains Mono, monospace', userSelect: 'none',
    }}>PROOF</div>
  </div>
);

const CardCanvas = ({
  cardData = {},
  tier = 'signature',
  watermark = true,
  scale = 1,
  showStockBadge = true,
  side = 'both',
  fontPairing = null,           // WPS-018 · pass a font pairing doc
  showGuides = false,           // WPS-018 · trim + safe zone overlay
  className = '',
}) => {
  const t = TIER_STYLES[tier] || TIER_STYLES.signature;
  const orientation = (cardData.orientation || 'horizontal').toLowerCase();
  const hasBack = cardData.backDesign?.enabled !== false;
  const shouldRenderBack = (side === 'both' || side === 'back') && hasBack;
  const shouldRenderFront = side === 'both' || side === 'front';

  return (
    <div className={`card-canvas-wrapper ${className}`}>
      <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', justifyContent: 'center' }}>
        {shouldRenderFront && (
          <div style={{ position: 'relative' }}>
            <CardFront cardData={cardData} tier={tier} watermark={watermark} scale={scale} orientation={orientation} fontPairing={fontPairing} showGuides={showGuides} />
            <SideLabel text={`Front · ${orientation}`} />
          </div>
        )}
        {shouldRenderBack && (
          <div style={{ position: 'relative' }}>
            <CardBack cardData={cardData} tier={tier} watermark={watermark} scale={scale} orientation={orientation} fontPairing={fontPairing} showGuides={showGuides} />
            <SideLabel text={`Back · ${orientation}`} />
          </div>
        )}
      </div>

      {showStockBadge && (
        <div style={{
          marginTop: '16px',
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: '10px', letterSpacing: '0.12em',
          color: '#475569', textTransform: 'uppercase', textAlign: 'center',
        }}>{t.label} · {t.stock} · {orientation === 'vertical' ? '2.125" × 3.375"' : '3.375" × 2.125"'}</div>
      )}
    </div>
  );
};

const SideLabel = ({ text }) => (
  <div style={{
    marginTop: '8px',
    fontFamily: 'JetBrains Mono, monospace',
    fontSize: '10px', letterSpacing: '0.15em',
    color: '#94a3b8', textTransform: 'uppercase', textAlign: 'center',
  }}>{text}</div>
);

export const CardCanvasViewer = ({
  cardData,
  tier = 'signature',
  watermark = true,
  allowTierSwitch = false,
  allowOrientationSwitch = false,
  fontPairing = null,
}) => {
  const [activeTier, setActiveTier] = useState(tier);
  const [scale, setScale] = useState(1);
  const [side, setSide] = useState('both');
  const [orientation, setOrientation] = useState(cardData.orientation || 'horizontal');
  const [guides, setGuides] = useState(true);

  const effectiveData = { ...cardData, orientation };

  return (
    <div style={{
      padding: '24px', background: '#f1f5f9',
      borderRadius: 0, border: '1px solid #e2e8f0',
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', marginBottom: '16px',
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: '11px', color: '#475569',
        letterSpacing: '0.12em', textTransform: 'uppercase',
        flexWrap: 'wrap', gap: '12px',
      }}>
        <span>1:1 Preview · {orientation === 'vertical' ? '2.125" × 3.375"' : '3.375" × 2.125"'}</span>
        <div style={{ display: 'flex', gap: '6px' }}>
          {['both', 'front', 'back'].map(s => (
            <button key={s} onClick={() => setSide(s)} style={{
              ...btn,
              background: side === s ? '#0f172a' : '#ffffff',
              color: side === s ? '#fff' : '#0f172a',
              padding: '5px 10px',
            }}>{s}</button>
          ))}
        </div>
        <button onClick={() => setGuides(!guides)} style={{
          ...btn,
          background: guides ? '#01579b' : '#ffffff',
          color: guides ? '#fff' : '#0f172a',
        }}>{guides ? '✓ Guides on' : 'Show guides'}</button>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button onClick={() => setScale(s => Math.max(0.6, +(s - 0.2).toFixed(2)))} style={btn}>−</button>
          <span style={{ minWidth: '44px', textAlign: 'center' }}>{Math.round(scale * 100)}%</span>
          <button onClick={() => setScale(s => Math.min(2.5, +(s + 0.2).toFixed(2)))} style={btn}>+</button>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
        <CardCanvas
          cardData={effectiveData}
          tier={activeTier}
          watermark={watermark}
          scale={scale}
          side={side}
          fontPairing={fontPairing}
          showGuides={guides}
        />
      </div>

      <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '12px', flexWrap: 'wrap' }}>
        {allowOrientationSwitch && (
          <div style={{ display: 'flex', gap: '6px' }}>
            {['horizontal', 'vertical'].map(o => (
              <button key={o} onClick={() => setOrientation(o)} style={{
                ...btn,
                background: orientation === o ? '#475569' : '#ffffff',
                color: orientation === o ? '#fff' : '#0f172a',
                textTransform: 'capitalize',
              }}>{o}</button>
            ))}
          </div>
        )}
        {allowTierSwitch && (
          <div style={{ display: 'flex', gap: '6px' }}>
            {['signature', 'atelier', 'executive'].map(tk => (
              <button key={tk} onClick={() => setActiveTier(tk)} style={{
                ...btn,
                background: activeTier === tk ? '#01579b' : '#ffffff',
                color: activeTier === tk ? '#fff' : '#0f172a',
                borderColor: activeTier === tk ? '#01579b' : '#e2e8f0',
                textTransform: 'capitalize',
              }}>{tk}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const btn = {
  border: '1px solid #e2e8f0',
  background: '#ffffff',
  padding: '6px 12px',
  fontFamily: 'JetBrains Mono, monospace',
  fontSize: '11px',
  letterSpacing: '0.08em',
  cursor: 'pointer',
  borderRadius: 0,
  color: '#0f172a',
};

export default CardCanvas;
